import { GenericProductDto, PaymentsProductDto, PaymentTypeEnum, PricingQuoteIncludedProductDto, ProductTypeEnum, ProductV2Dto, ScreeningProductPricingDto } from '@inosio/payments-sdk';

export abstract class BaseProductPreferenceValidator {
  //available products either from the package or from the default product list for custom flow
  constructor(protected readonly availableProducts: ProductV2Dto[]) {}

  /**
   * Validation function to determine if all required fields are correctly populated for the preference object   *
   * @param preferenceObject
   * @returns true if all good. false otherwise.
   */
  public abstract IsPreferenceObjectComplete(preferenceObject: PricingQuoteIncludedProductDto | undefined): boolean;
}

export class PaymentsPreferencesValidator extends BaseProductPreferenceValidator {
  public IsPreferenceObjectComplete(preferenceObject: PricingQuoteIncludedProductDto | undefined): boolean {
    if (!preferenceObject) return true; //if the product isn't included then no need to validate, its good

    //Step 1, make sure the product selected is actually a valid product on the menu
    const availablePaymentsProduct = this.availableProducts.find((prod) => prod.ProductType === ProductTypeEnum.Payments)?.Payload?.[0] as PaymentsProductDto;

    const isSelectedProductValid = availablePaymentsProduct.ProductUuid === preferenceObject.ProductUuid;

    //Step 2, make sure they have configured all payment types
    const selectedPaymentOptions = preferenceObject.PaymentOptions || [];

    // Get all payment types in the list
    const paymentTypes = selectedPaymentOptions.map((po) => po.PaymentType);

    // Create a Set to ensure each type is unique
    const distinctPaymentTypes = new Set(paymentTypes);

    // Ensure there are exactly 3 payment types
    const areSelectedPaymentOptionsValid =
      distinctPaymentTypes.has(PaymentTypeEnum.Ach) &&
      distinctPaymentTypes.has(PaymentTypeEnum.Cc) &&
      distinctPaymentTypes.has(PaymentTypeEnum.Db) &&
      distinctPaymentTypes.size === 3 &&
      paymentTypes.length === 3;

    return isSelectedProductValid && areSelectedPaymentOptionsValid;
  }
}

export class ScreeningPreferencesValidator extends BaseProductPreferenceValidator {
  public IsPreferenceObjectComplete(preferenceObject: PricingQuoteIncludedProductDto | undefined): boolean {
    if (!preferenceObject) return true; //if the product isn't included then no need to validate, its good

    //only need to make sure the product selected is actually a valid product on the menu
    const availableScreeningProducts = this.availableProducts.find((prod) => prod.ProductType === ProductTypeEnum.Screening)?.Payload as ScreeningProductPricingDto[];

    const isSelectedProductValid = availableScreeningProducts.find((prod) => prod.ProductUuid === preferenceObject.ProductUuid);

    return !!isSelectedProductValid && preferenceObject.hasOwnProperty('AutoScreening');
  }
}

export class MaintenancePreferencesValidator extends BaseProductPreferenceValidator {
  public IsPreferenceObjectComplete(preferenceObject: PricingQuoteIncludedProductDto | undefined): boolean {
    if (!preferenceObject) return true; //if the product isn't included then no need to validate, its good

    //only need to make sure the product selected is actually a valid product on the menu
    const availableMaintenanceProducts = this.availableProducts.find((prod) => prod.ProductType === ProductTypeEnum.Maintenance)?.Payload as GenericProductDto[];

    const isSelectedProductValid = availableMaintenanceProducts.find((prod) => prod.ProductUuid === preferenceObject.ProductUuid);

    return !!isSelectedProductValid;
  }
}

export class OwnerPortalPreferencesValidator extends BaseProductPreferenceValidator {
  public IsPreferenceObjectComplete(preferenceObject: PricingQuoteIncludedProductDto | undefined): boolean {
    if (!preferenceObject) return true; //if the product isn't included then no need to validate, its good

    //only need to make sure the product selected is actually a valid product on the menu
    const availableOwnerPortalProducts = this.availableProducts.find((prod) => prod.ProductType === ProductTypeEnum.OwnerPortal)?.Payload as GenericProductDto[];

    const isSelectedProductValid = availableOwnerPortalProducts.find((prod) => prod.ProductUuid === preferenceObject.ProductUuid);

    return !!isSelectedProductValid;
  }
}

export class ApplicationPreferencesValidator extends BaseProductPreferenceValidator {
  public IsPreferenceObjectComplete(preferenceObject: PricingQuoteIncludedProductDto | undefined): boolean {
    if (!preferenceObject) return true; //if the product isn't included then no need to validate, its good

    //only need to make sure the product selected is actually a valid product on the menu
    const availableApplicationProducts = this.availableProducts.find((prod) => prod.ProductType === ProductTypeEnum.Applications)?.Payload as GenericProductDto[];

    const isSelectedProductValid = availableApplicationProducts.find((prod) => prod.ProductUuid === preferenceObject.ProductUuid);

    return !!isSelectedProductValid;
  }
}
